/** Generic renderer for every CONTENT/OUTPUT "asset/result" node
 * (Character, Dialogue, Voice, Keyframe, Reference Content, Virtual IP,
 * Video/Image Asset, Production Artifact, Performance, ...). Driven
 * entirely by the Node Registry — spec §5/§10 "avoid a giant switch
 * statement" and "asset/result node vs. generation/action node" are both
 * satisfied by having exactly one component per family instead of one
 * per node type. */

import { Handle, Position, type NodeProps } from '@xyflow/react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import OfflineNodeBadge from '../OfflineNodeBadge'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import type { FlowNodeData } from '../types'

function EntityNodeImpl({ data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData
  const def = useNodeDefinition(nodeData.nodeType)
  const label = nodeInstanceLabel(nodeData.nodeType, nodeData.fields?.label as string | undefined, def.label)
  const preview = previewText(nodeData)

  return (
    <div className={`pcv-node pcv-node--entity${selected ? ' is-selected' : ''}`} style={{ borderColor: selected ? def.color : undefined }}>
      <Handle type="target" position={Position.Left} className="pcv-handle" />
      <div className="pcv-node-header">
        <span className="pcv-node-icon" style={{ color: def.color, background: `${def.color}22` }}>
          {def.icon}
        </span>
        <span className="pcv-node-title" title={label}>
          {label}
        </span>
        <OfflineNodeBadge nodeType={nodeData.nodeType} />
      </div>
      {preview ? <div className="pcv-node-preview">{preview}</div> : null}
      {!nodeData.entityId ? <div className="pcv-node-badge">{t('entity.draft')}</div> : null}
      <Handle type="source" position={Position.Right} className="pcv-handle" />
    </div>
  )
}

function previewText(data: FlowNodeData): string | null {
  const fields = data.fields || {}
  if (typeof fields.text === 'string' && fields.text) return fields.text
  if (typeof fields.summary === 'string' && fields.summary) return fields.summary
  if (typeof fields.speaker === 'string' && fields.speaker) return `speaker: ${fields.speaker}`
  return null
}

export default memo(EntityNodeImpl)
